import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { rhm, money, dOnly } from "../api";
import { Card, Empty, PageHead, Select, StatCard, Table, Td } from "../ui";

const PERIODS = [
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "365d", label: "Last 12 months" },
];

export default function RhmReports() {
    const [period, setPeriod] = useState("30d");
    const { data, isLoading } = useQuery({
        queryKey: ["rhm-reports", period],
        queryFn: async () => (await rhm.get("/reports", { params: { period } })).data,
    });

    if (isLoading) return <div className="mono text-xs text-zinc-500">BUILDING REPORTS…</div>;
    const s = data?.sales || {};

    const exportCsv = () => {
        const rows = [["Date", "Orders", "Revenue"], ...(s.by_day || []).map((d) => [d.date, d.orders, d.revenue])];
        const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `rhm-sales-${period}.csv`;
        a.click();
    };

    return (
        <div data-testid="rhm-reports-page">
            <PageHead title="Reports" sub={`${data?.range?.start} → ${data?.range?.end}`}
                action={
                    <div className="flex gap-3">
                        <Select value={period} onChange={(e) => setPeriod(e.target.value)} options={PERIODS} className="!w-44" data-testid="report-period" />
                        <button onClick={exportCsv} data-testid="report-export"
                            className="mono text-[11px] tracking-[0.14em] px-4 py-2.5 border border-white/15 hover:border-gold hover:text-gold">
                            EXPORT CSV
                        </button>
                    </div>
                } />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testId="report-orders" label="Orders" value={s.orders ?? 0} />
                <StatCard testId="report-revenue" label="Revenue" value={money(s.revenue)} tone="gold" />
                <StatCard label="Average Order Value" value={money(s.average_order_value)} />
                <StatCard label="Discount Given" value={money(s.discount_given)} tone="good" />
            </div>

            <div className="grid lg:grid-cols-2 gap-5 mt-6">
                <Card title="Sales by Day">
                    <Table head={["Date", "Orders", "Revenue"]} empty={!s.by_day?.length && <Empty msg="No sales in this range." />}>
                        {(s.by_day || []).map((d) => (
                            <tr key={d.date}>
                                <Td className="text-sm">{dOnly(d.date)}</Td>
                                <Td>{d.orders}</Td>
                                <Td>{money(d.revenue)}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>

                <Card title="Best Selling Products">
                    <Table head={["Product", "Units", "Revenue"]} empty={!data?.products?.best_selling?.length && <Empty msg="No product sales yet." />}>
                        {(data?.products?.best_selling || []).map((p) => (
                            <tr key={p.product}>
                                <Td className="font-semibold">{p.product}</Td>
                                <Td>{p.units}</Td>
                                <Td>{money(p.revenue)}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>

                <Card title="Inventory Valuation">
                    <Table head={["Product", "Available", "Min", "Value at MRP"]} empty={!data?.inventory?.length && <Empty msg="No stock." />}>
                        {(data?.inventory || []).map((r) => (
                            <tr key={r.product_code}>
                                <Td className="font-semibold">{r.product}</Td>
                                <Td className={r.available <= r.min_stock ? "text-red-400" : ""}>{r.available}</Td>
                                <Td className="text-zinc-500">{r.min_stock}</Td>
                                <Td>{money(r.value_at_mrp)}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>

                <Card title="Customers">
                    <div className="grid grid-cols-2 gap-4 mb-5">
                        {[["Total", data?.customers?.total], ["New in range", data?.customers?.new_in_range],
                          ["Repeat customers", data?.customers?.repeat_customers], ["Repeat rate", `${data?.customers?.repeat_rate ?? 0}%`]].map(([l, v]) => (
                            <div key={l} className="border border-white/10 p-4">
                                <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">{l.toUpperCase()}</div>
                                <div className="font-display text-2xl mt-1">{v ?? 0}</div>
                            </div>
                        ))}
                    </div>
                    <Table head={["Customer", "Orders", "Lifetime Value"]} empty={!data?.customers?.top_customers?.length && <Empty msg="No customers yet." />}>
                        {(data?.customers?.top_customers || []).map((c) => (
                            <tr key={c.name}>
                                <Td className="font-semibold">{c.name}</Td>
                                <Td>{c.orders}</Td>
                                <Td>{money(c.value)}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>

                <Card title="Dealer Performance">
                    <Table head={["Dealer", "Orders", "Value", "Last Order", "Status"]} empty={!data?.dealers?.length && <Empty msg="No dealers yet." />}>
                        {(data?.dealers || []).map((d) => (
                            <tr key={d.dealer}>
                                <Td className="font-semibold">{d.dealer}</Td>
                                <Td>{d.orders}</Td>
                                <Td>{money(d.value)}</Td>
                                <Td className="text-sm">{d.last_order ? dOnly(d.last_order) : "—"}</Td>
                                <Td className="text-sm text-zinc-400">{d.status}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>

                <Card title="Offer Performance">
                    <Table head={["Code", "Offer", "Times Used", "Discount Given"]} empty={!data?.offers?.length && <Empty msg="No offers yet." />}>
                        {(data?.offers || []).map((o) => (
                            <tr key={o.code}>
                                <Td className="mono text-xs text-gold">{o.code}</Td>
                                <Td>{o.name}</Td>
                                <Td>{o.used}</Td>
                                <Td>{money(o.discount_given)}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>
            </div>
        </div>
    );
}
